import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  IncrementalMinibeakHookResult,
  MinibeakConfig,
  MinibeakHookResult,
  MinibeakResourceLookup,
  MinibeakResult,
  MinibeakResultData,
  ProFileRawResponse,
  ProFileResource,
  ProFileResourceRelationship,
  RawProFileResource,
  RawResponseData,
} from "./types";
import { VERSION } from "./version";

if (typeof localStorage !== "undefined") {
  localStorage.setItem("MINIBEAK_VERSION", VERSION);
}

export type {
  IncrementalMinibeakHookResult,
  MinibeakConfig,
  MinibeakHookResult,
  MinibeakResourceLookup,
  MinibeakResult,
  MinibeakResultData,
  ProFileRawResponse,
  ProFileResource,
  ProFileResourceRelationship,
  RawProFileResource,
  RawResponseData,
  StrictProFileResource,
} from "./types";
export { VERSION };

/**
 * Starting point for request configuration. Spread it into the config of every
 * call, for example `useProFile({ ...defaultConfig, path: "/orgUnits" })`.
 *
 * It is frozen. Build your own frozen config object from it instead of
 * mutating it. Minibeak never applies it implicitly, neither in the hooks nor
 * in callProFile.
 */
export const defaultConfig: Readonly<MinibeakConfig> = Object.freeze({
  apiUrl: "/profile/jsonapi",
});

let callCache: Record<string, ProFileRawResponse> = {};
let cacheTagBackreferences: Record<string, Record<string, boolean>> = {}; // maps cache tags to cache keys
const cacheTimeoutHandles: Record<string, number> = {};

type CacheClearable = string | MinibeakConfig;

export function clearCallCache(
  configsOrTags?: boolean | CacheClearable | Array<CacheClearable>
): void {
  if (!configsOrTags) {
    callCache = {};
    cacheTagBackreferences = {};
  } else {
    forEach(configsOrTags, (configOrTag) => {
      if (typeof configOrTag === "string") {
        // cache tag

        if (!cacheTagBackreferences[configOrTag]) return;

        for (const cacheKey in cacheTagBackreferences[configOrTag]) {
          delete callCache[cacheKey];
        }

        // everything referenced by this tag has been deleted.
        // we can forget the tag, because it will be recreated
        // when any of those resources are repopulated.
        delete cacheTagBackreferences[configOrTag];
      } else {
        // config object
        const cacheKey = JSON.stringify(configOrTag);
        delete callCache[cacheKey];
      }
    });
  }
}

export class ResponseNotOkError extends Error {
  constructor(readonly response: Response) {
    const pathname = response.url ? new URL(response.url).pathname : "";
    super(`${response.status} ${pathname}`);
    this.name = "ResponseNotOkError";
    // The library is compiled to ES5, where extending Error loses the subclass
    // prototype. Restore it so instanceof works for consumers.
    Object.setPrototypeOf(this, ResponseNotOkError.prototype);
  }
}

export async function callProFile<ResponseType = RawResponseData>(
  config: MinibeakConfig = {},
  forceRefresh?: boolean
): Promise<ResponseType> {
  const {
    include,
    path,
    apiUrl,
    minibeakCache,
    queryParams = {},
    throwErrors,
    shouldExecute = true,
    ...fetchOpts
  } = config;

  const cacheKey = minibeakCache ? JSON.stringify(config) : null;

  if (cacheKey) {
    const hit = callCache[cacheKey];
    if (hit && !forceRefresh) return hit as ResponseType;
  }

  if (!apiUrl) {
    throw new Error("Missing apiUrl in config");
  }

  if (!shouldExecute) {
    throw new Error(
      "shouldExecute is not supported in callProFile, callProFile can be called conditionally out of the box"
    );
  }

  if (!path) {
    throw new Error("Missing path in config");
  }

  const apiUrlWithProtocol = apiUrl.includes("://")
    ? apiUrl
    : window.location.protocol + "//" + window.location.host + apiUrl;

  if (include) {
    const includeParams = compressIncludeList(include, true);
    queryParams.include = includeParams.include;

    if (includeParams.includeAlias) {
      queryParams.includeAlias = includeParams.includeAlias;
    }
  }

  let queryParamsString = Object.keys(queryParams)
    .map(
      (key) =>
        encodeURIComponent(key) +
        "=" +
        encodeURIComponent(queryParams[key] + "")
    )
    .join("&");

  const queryUrl =
    apiUrlWithProtocol +
    (path.startsWith("/") ? path : `/${path}`) +
    (queryParamsString && "?") +
    queryParamsString;

  // stringify body if set and not string
  if (
    Object.prototype.hasOwnProperty.call(fetchOpts, "body") &&
    typeof fetchOpts.body !== "string"
  ) {
    fetchOpts.body = JSON.stringify(fetchOpts.body);
  }

  // set content-type if not set and there is a body
  if (
    fetchOpts.body &&
    //@ts-ignore
    (!fetchOpts.headers || !fetchOpts.headers["Content-Type"])
  ) {
    if (!fetchOpts.headers) fetchOpts.headers = {};
    //@ts-ignore
    fetchOpts.headers["Content-Type"] = "application/vnd.api+json";
  }

  const response = await fetch(queryUrl, {
    credentials: "include",
    ...fetchOpts,
  });

  if (!response.ok) {
    throw new ResponseNotOkError(response);
  }

  const responseContentType = response.headers.get("content-type");

  const result =
    responseContentType && responseContentType.match("json$")
      ? await response.json()
      : response;

  if (cacheKey) {
    callCache[cacheKey] = result;

    let lowestTimeout = Infinity;

    forEach(minibeakCache, (boolOrNumOrString) => {
      if (typeof boolOrNumOrString === "number") {
        const num = boolOrNumOrString;
        if (num < lowestTimeout) lowestTimeout = num;
      } else if (typeof boolOrNumOrString === "string") {
        const tag = boolOrNumOrString;
        if (!cacheTagBackreferences[tag]) {
          cacheTagBackreferences[tag] = {};
        }
        cacheTagBackreferences[tag][cacheKey] = true;
      }
      // else do nothing
    });

    if (lowestTimeout !== Infinity && !cacheTimeoutHandles[cacheKey]) {
      cacheTimeoutHandles[cacheKey] = setTimeout(() => {
        clearCallCache(config);
        delete cacheTimeoutHandles[cacheKey];
      }, lowestTimeout * 1000);
    }
  }

  return result as ResponseType;
}

type CompressedIncludes = {
  include: string;
  includeAlias?: string;
};

// Compresses a list of include paths by converting path tokens into shorter aliases.
// Copied and modified from Thomas' version in ProFile.
export function compressIncludeList(
  includes: Array<string>, // List of includes to compress
  preserveEndTokens = false, // If true, the last token of each include path will be preserved (unless matching an alias)
  occurrencesNeeded = 2 // The number of token string occurrences required for an alias to be registered
): CompressedIncludes {
  const includeList: string[] = [];
  const includeAlias: string[] = [];

  // Map tokens
  const tokenMap: any = {};

  includes.forEach((include) => {
    const tokens = include.split(".");

    tokens.forEach((token, tokenIndex) => {
      // Ignore last token if specified
      if (preserveEndTokens && tokenIndex === tokens.length - 1) {
        return false;
      }

      // If the token has not yet been registered, give it a new alias
      if (!tokenMap[token]) {
        tokenMap[token] = {
          occurrences: 0,
        };
      }
      // Otherwise, just increase the occurrence count
      else {
        tokenMap[token].occurrences++;
      }
    });
  });

  // Generate list of aliases for tokens occurring more than the required number
  let aliasIndex = 0;
  Object.keys(tokenMap).forEach((token) => {
    const tokenData = tokenMap[token];
    if (tokenData.occurrences >= occurrencesNeeded) {
      tokenData.alias = aliasIndex++;
      includeAlias.push(tokenData.alias + "=" + token);
    }
  });

  // Replace tokens with aliases if occurring more than the required number
  includes.forEach((include) => {
    let compressedInclude = include;

    Object.keys(tokenMap).forEach((token) => {
      const tokenData = tokenMap[token];
      if (tokenData.occurrences >= occurrencesNeeded) {
        compressedInclude = compressedInclude.replace(
          new RegExp("(^|\\.)" + token + "(\\.|$)", "g"),
          "$1" + tokenData.alias + "$2"
        );
      }
    });

    includeList.push(compressedInclude);
  });

  const result: CompressedIncludes = {
    include: includeList.join(","),
  };

  if (includeAlias.length) {
    result.includeAlias = includeAlias.join(",");
  }

  return result;
}

function forEach<T>(itemOrArray: T | Array<T>, fn: (item: T) => void) {
  if (typeof itemOrArray === "undefined") return;
  return Array.isArray(itemOrArray) ? itemOrArray.forEach(fn) : fn(itemOrArray);
}

function map<T, U>(
  itemOrArray: T,
  fn: (item: T extends unknown[] ? T[number] : T) => U
): T extends unknown[] ? U[] : U {
  return (Array.isArray(itemOrArray)
    ? itemOrArray.map(fn)
    : fn(itemOrArray as any)) as any;
}

function getResourceUniqueId(
  resource: RawProFileResource | ProFileResourceRelationship
) {
  return resource.compositeId || resource.id;
}

function resolveRelationships(lookup: MinibeakResourceLookup) {
  for (const resourceTypeName in lookup) {
    if (Object.prototype.hasOwnProperty.call(lookup, resourceTypeName)) {
      const resourcesOfType = lookup[resourceTypeName];

      for (const resourceUniqueId in resourcesOfType) {
        if (
          Object.prototype.hasOwnProperty.call(
            resourcesOfType,
            resourceUniqueId
          )
        ) {
          const resource = resourcesOfType[resourceUniqueId];

          const { $relationships } = resource;

          if ($relationships) {
            Object.keys($relationships).forEach((relationshipName) => {
              resource[relationshipName] = map(
                $relationships[relationshipName].data,
                (relationshipData) => {
                  const includedWithType = lookup[relationshipData.type];
                  return includedWithType?.[
                    getResourceUniqueId(relationshipData)
                  ];
                }
              );
            });
          }
        }
      }
    }
  }
}

function frontendize(resource: RawProFileResource): ProFileResource {
  const { type, lazy, attributes, relationships, ...rest } = resource;

  const result: ProFileResource = {
    $type: type,
    ...rest,
    ...attributes,
  };

  if (lazy) result.$lazy = lazy;
  if (relationships) result.$relationships = relationships;

  return result;
}

function addToLookup(
  data: RawProFileResource | RawProFileResource[],
  lookup: MinibeakResourceLookup
) {
  forEach(data, (resource) => {
    const { type } = resource;
    const uniqueId = getResourceUniqueId(resource);

    if (!lookup[type]) lookup[type] = {};
    const resourceLookup = lookup[type]!!;

    // If resource has already been processed merge relationships.
    // This can happen if the same resource is included through different paths
    if (!resourceLookup[uniqueId]) {
      resourceLookup[uniqueId] = frontendize(resource);
    } else if (resourceLookup[uniqueId].$relationships) {
      // TODO: merge intro resource lookup relationships if the same resource was present several times?
    }
  });
}

export function processProFileResult<
  DataType extends MinibeakResultData = MinibeakResultData,
  ResourceLookupType extends MinibeakResourceLookup = MinibeakResourceLookup
>({
  data,
  included,
}: ProFileRawResponse): MinibeakResult<DataType, ResourceLookupType> {
  const resources: MinibeakResourceLookup = {};

  if (data) addToLookup(data, resources);
  if (included) addToLookup(included, resources);

  resolveRelationships(resources);

  const dataMappedToMinibeakResources = data
    ? map(
        data,
        (resource) =>
          resource && resources[resource.type]?.[getResourceUniqueId(resource)]
      )
    : [];

  return {
    data: dataMappedToMinibeakResources as DataType,
    resources: resources as ResourceLookupType,
  };
}

async function responseToMinibeakResultAsync<
  DataType extends MinibeakResultData,
  ResourceLookupType extends MinibeakResourceLookup
>(
  responsePromise: Promise<ProFileRawResponse>
): Promise<Omit<MinibeakHookResult<DataType, ResourceLookupType>, "refresh">> {
  try {
    const response = await responsePromise;

    const { data, resources } = processProFileResult<
      DataType,
      ResourceLookupType
    >(response);

    return {
      data,
      response,
      resources,
      isReady: true,
      isFetching: false,
    };
  } catch (error) {
    return {
      error,
      isReady: true,
      isFetching: false,
    };
  }
}

export function useProFile<
  DataType extends MinibeakResultData = ProFileResource | ProFileResource[],
  ResourceLookupType extends MinibeakResourceLookup = MinibeakResourceLookup
>(config: MinibeakConfig): MinibeakHookResult<DataType, ResourceLookupType> {
  const [result, setResult] = useState<
    Omit<MinibeakHookResult<DataType, ResourceLookupType>, "refresh">
  >({
    isReady: false,
    isFetching: true,
  });

  const configKey = JSON.stringify(config);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const memoedConfig = useMemo(() => config, [configKey]);

  const fetchFromEndpoint = useCallback(
    async (forceRefresh?: boolean) => {
      if (!memoedConfig.path || memoedConfig.shouldExecute === false) return;

      setResult((oldResult) => ({ ...oldResult, isFetching: true }));

      const response = callProFile<RawResponseData>(
        { ...memoedConfig, shouldExecute: true },
        forceRefresh
      );
      const result = await responseToMinibeakResultAsync<
        DataType,
        ResourceLookupType
      >(response);
      setResult(result);

      return result;
    },
    [memoedConfig]
  );

  useEffect(() => {
    fetchFromEndpoint();
  }, [fetchFromEndpoint]);

  const refresh = useCallback(() => fetchFromEndpoint(true), [
    fetchFromEndpoint,
  ]);

  const hookResult = useMemo<MinibeakHookResult<DataType, ResourceLookupType>>(
    () => ({
      ...result,
      refresh,
    }),
    [result, refresh]
  );

  if (memoedConfig.throwErrors && hookResult.error) {
    // Thrown during render so the nearest React error boundary catches it.
    throw hookResult.error;
  }

  return hookResult;
}

/** Supports making incremental calls to fetch more and more resources. This can be used to implement "infinite scolling". */
export function useProFileIncrementally<
  DataType extends ProFileResource[] = ProFileResource[],
  ResourceLookupType extends MinibeakResourceLookup = MinibeakResourceLookup
>(
  baseConfig: MinibeakConfig
): IncrementalMinibeakHookResult<DataType, ResourceLookupType> {
  if (typeof baseConfig.queryParams?.max !== "number") {
    throw new Error(
      "useProFileIncrementally: You must include a value for max in queryParams in the config."
    );
  }

  if (typeof baseConfig.queryParams?.offset === "number") {
    throw new Error(
      "useProFileIncrementally: You should not provide an offset. It will be calculated on our side."
    );
  }
  const offset = useRef(0);

  const baseConfigKey = JSON.stringify(baseConfig);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const memoedBaseConfig = useMemo(() => baseConfig, [baseConfigKey]);

  const getConfig = useCallback(
    (offset: number): MinibeakConfig => {
      return {
        ...memoedBaseConfig,
        queryParams: {
          ...(memoedBaseConfig.queryParams || {}),
          offset,
        },
      };
    },
    [memoedBaseConfig]
  );

  const [results, setResults] = useState<
    MinibeakHookResult<DataType, ResourceLookupType>[]
  >([]);

  const loadNextPage = useCallback(async () => {
    if (!memoedBaseConfig.path || memoedBaseConfig.shouldExecute === false)
      return;

    const initialResult: MinibeakHookResult<DataType, ResourceLookupType> = {
      isReady: false,
      isFetching: true,
      refresh: () => Promise.resolve(),
    };

    // the refresh callback closes over this variable to remember which result to replace
    let result = initialResult;

    setResults((old) => [...old, result]);

    const offsetOfCall = offset.current;
    offset.current += memoedBaseConfig.queryParams?.max as number;

    async function refresh() {
      if (result !== initialResult) {
        const replacedResult = result;
        setResults((old) => {
          return old.map((item) =>
            item === replacedResult ? initialResult : item
          );
        });
        result = initialResult;
      }

      const config = { ...getConfig(offsetOfCall), shouldExecute: true };

      const response = callProFile<RawResponseData>(config);

      const newResultWithoutRefresh = await responseToMinibeakResultAsync<
        DataType,
        ResourceLookupType
      >(response);

      const oldResult = result;
      result = { ...newResultWithoutRefresh, refresh };
      setResults((old) =>
        old.map((item) => (item === oldResult ? result : item))
      );

      return result;
    }

    return refresh();
  }, [getConfig, memoedBaseConfig]);

  useEffect(() => {
    setResults([]);
    offset.current = 0;
    loadNextPage();
  }, [loadNextPage, getConfig]);

  const joinedData = useMemo(() => {
    const joinedData = ([] as unknown) as DataType;
    results.forEach((result) => {
      if (result.data) {
        joinedData.push(...result.data);
      }
    });
    return joinedData;
  }, [results]);

  const isFetching = useMemo(
    () =>
      results.length ? !!results.find((result) => result.isFetching) : true,
    [results]
  );

  const error = useMemo(() => {
    return results.find((result) => result.error)?.error;
  }, [results]);

  const totalResults = useMemo(() => {
    return (
      results.find(
        (result) => typeof result.response?.links?.total === "number"
      )?.response?.links?.total || 0
    );
  }, [results]);

  const max = baseConfig.queryParams.max as number;

  const fetchedResults = Math.min(results.length * max, totalResults);

  const hookResult = useMemo(
    () => ({
      isFetching,
      error,
      results,
      joinedData,
      loadNextPage,
      totalResults,
      fetchedResults,
      remainingResults: totalResults - fetchedResults,
    }),
    [
      isFetching,
      error,
      results,
      joinedData,
      loadNextPage,
      totalResults,
      fetchedResults,
    ]
  );

  if (memoedBaseConfig.throwErrors && error) {
    // Thrown during render so the nearest React error boundary catches it.
    throw error;
  }

  return hookResult;
}
